import React, { useState, useEffect } from "react";
import { UrlApi } from "../../../services/apirest";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import moment from "moment";
import CobrosPedido from "./CobrosPedido";
import TarjetaDetalle from "./TarjetaDetalle";

// Detalle del pedido (panel que se expande en la tabla de pedidos), en tarjetas:
// - Pedido: siempre (cliente, condición, domicilio, sucursal, requerido, tanque/batería, observaciones).
// - Rendición: asignado (23) y con la entrega registrada (a facturar y facturados).
// - Entrega: a facturar y facturados.
// - Visita: no entregado (41).
// - Cobros: registrados desde la app (granel_pagos), debajo de todo.

//Estados con la entrega registrada (a facturar y facturados): muestran entrega y rendición
const ESTADOS_CON_ENTREGA = [40, 243, 244, 248, 25, 263];
const ESTADO_ASIGNADO = 23;
const ESTADO_NO_ENTREGADO = 41;
const ESTADO_BAJA = 32;

const formatearFecha = (fecha) => (fecha && moment(fecha).isValid() ? moment(fecha).format("DD-MM-YYYY") : null);
const formatearNumero = (valor) =>
  valor === null || valor === undefined || valor === ""
    ? null
    : Number(valor).toLocaleString("es-AR", { maximumFractionDigits: 2 });
const formatearMonto = (valor) =>
  valor ? `$${Number(valor).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

// Par etiqueta / valor. No se muestra si no hay valor.
function Dato({ etiqueta, valor, ancho = false }) {
  if (valor === null || valor === undefined || valor === "") return null;
  return (
    <Box sx={{ gridColumn: ancho ? "1 / -1" : "auto", minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}
      >
        {etiqueta}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
        {valor}
      </Typography>
    </Box>
  );
}

// Datos en dos columnas
function Datos({ children }) {
  return <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1.25 }}>{children}</Box>;
}

// Nota destacada (observaciones)
function Nota({ titulo, texto }) {
  if (!texto) return null;
  return (
    <Box sx={{ mt: 1.5, p: 1, bgcolor: "#FEF9E7", borderLeft: "4px solid #F1C40F", borderRadius: 1 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <SpeakerNotesIcon sx={{ fontSize: 16, color: "#9A7D0A" }} />
        <Typography variant="caption" fontWeight={700} color="#9A7D0A">
          {titulo}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {texto}
      </Typography>
    </Box>
  );
}

function DetallePedido(props) {
  const [datosPedido] = useState(props.datos);
  const [reparto, setReparto] = useState(null);

  const estado = datosPedido?.id_estado;
  const conEntrega = ESTADOS_CON_ENTREGA.includes(estado);

  useEffect(() => {
    if ((props.datos.id_estado === ESTADO_ASIGNADO || ESTADOS_CON_ENTREGA.includes(props.datos.id_estado)) && props.datos.nro_reparto) {
      fetch(UrlApi + "/repartos/nro_reparto/" + props.datos.nro_reparto)
        .then((data) => data.json())
        .then((data) => setReparto(Array.isArray(data) ? data[0] || null : null))
        .catch(() => setReparto(null));
    }
  }, [props.datos.id_estado]);

  if (!datosPedido) {
    return <h5> Los datos del pedido no se encuentran disponibles.</h5>;
  }

  const tanqueOBateria = datosPedido.id_bateria
    ? {
        etiqueta: "Batería",
        valor: `N° ${datosPedido.nro_bateria} (${datosPedido.nombre_bateria || "Nombre no registrado"})`,
      }
    : datosPedido.id_tanque
      ? { etiqueta: "Tanque N° serie", valor: datosPedido.nro_serie }
      : null;

  const entregado = datosPedido.ltsentregados && !datosPedido.kgentregados
    ? `${formatearNumero(datosPedido.ltsentregados)} lts`
    : datosPedido.kgentregados && !datosPedido.ltsentregados
      ? `${formatearNumero(datosPedido.kgentregados)} kg`
      : null;

  //Resumen de cobro guardado en el pedido (cobros anteriores a granel_pagos)
  const hayResumenCobro =
    datosPedido.importe_efectivo ||
    datosPedido.importe_cheque ||
    datosPedido.nro_cheque ||
    datosPedido.id_banco ||
    datosPedido.nro_factura ||
    datosPedido.nro_recibo;

  return (
    <Box sx={{ py: 1 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        {/* PEDIDO */}
        <TarjetaDetalle icono={<PersonIcon fontSize="small" />} titulo="Pedido">
          <Datos>
            <Dato etiqueta="Cliente" valor={datosPedido.cliente} ancho />
            <Dato etiqueta="Condición de pago" valor={datosPedido.condicion || "No registrada"} />
            <Dato
              etiqueta="Sucursal"
              valor={[datosPedido.sucursal_atiende, datosPedido.empresa].filter(Boolean).join(" - ")}
            />
            <Dato
              etiqueta="Domicilio de entrega"
              valor={
                datosPedido.direccion
                  ? `${datosPedido.direccion}${
                      datosPedido.nombre_direccion || datosPedido.lugar
                        ? ` (${datosPedido.nombre_direccion || datosPedido.lugar})`
                        : ""
                    }`
                  : null
              }
              ancho
            />
            <Dato
              etiqueta="Requerido"
              valor={datosPedido.kgacargar ? `${formatearNumero(datosPedido.kgacargar)} kg` : null}
            />
            <Dato etiqueta="Fecha requerida" valor={formatearFecha(datosPedido.fecha_requerida)} />
            {tanqueOBateria ? <Dato etiqueta={tanqueOBateria.etiqueta} valor={tanqueOBateria.valor} /> : null}
          </Datos>
          <Nota titulo="Observaciones" texto={datosPedido.observaciones} />
          {estado === ESTADO_BAJA ? <Nota titulo="Motivo de baja" texto={datosPedido.baja_obs} /> : null}
        </TarjetaDetalle>

        {/* ENTREGA (a facturar y facturados) */}
        {conEntrega ? (
          <TarjetaDetalle icono={<LocalShippingIcon fontSize="small" />} titulo="Entrega">
            <Datos>
              <Dato etiqueta="Entregado" valor={entregado} />
              <Dato etiqueta="Remito" valor={datosPedido.nro_remito} />
              <Dato etiqueta="Fecha de entrega" valor={formatearFecha(datosPedido.fecha_real_entrega)} />
              <Dato etiqueta="Entregado por" valor={datosPedido.chofer} />
            </Datos>
            {hayResumenCobro ? (
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: "1px dashed", borderColor: "divider" }}>
                <Datos>
                  <Dato etiqueta="Efectivo" valor={formatearMonto(datosPedido.importe_efectivo)} />
                  <Dato etiqueta="Cheque" valor={formatearMonto(datosPedido.importe_cheque)} />
                  <Dato etiqueta="N° cheque" valor={datosPedido.nro_cheque} />
                  <Dato etiqueta="Banco" valor={datosPedido.nombre} />
                  <Dato etiqueta="Recibo" valor={datosPedido.nro_recibo} />
                  <Dato etiqueta="Factura" valor={datosPedido.nro_factura} />
                </Datos>
              </Box>
            ) : null}
          </TarjetaDetalle>
        ) : null}

        {/* RENDICIÓN (asignado, a facturar y facturados) */}
        {(estado === ESTADO_ASIGNADO || conEntrega) && reparto?.id ? (
          <TarjetaDetalle icono={<AssignmentIcon fontSize="small" />} titulo="Rendición">
            <Datos>
              <Dato etiqueta="N° rendición" valor={reparto.id} />
              <Dato etiqueta="Fecha de salida" valor={formatearFecha(reparto.fecha_salida_reparto)} />
              <Dato
                etiqueta="Chofer"
                valor={
                  reparto.usuario_choferinicia
                    ? [reparto.nombrepila, reparto.apellido].filter(Boolean).join(" ") || reparto.usuario_choferinicia
                    : null
                }
              />
              <Dato etiqueta="Vehículo" valor={reparto.id_vehiculo ? reparto.patente : null} />
              <Dato etiqueta="Sucursal" valor={reparto.sucursal} />
              <Dato etiqueta="Chofer que entrega" valor={datosPedido.usuario_choferinicia} />
            </Datos>
          </TarjetaDetalle>
        ) : null}

        {/* VISITA (no entregado) */}
        {estado === ESTADO_NO_ENTREGADO ? (
          <TarjetaDetalle icono={<WhereToVoteIcon fontSize="small" />} titulo="Visita">
            <Datos>
              <Dato etiqueta="Visitado por" valor={datosPedido.chofer_entrego} ancho />
            </Datos>
            <Nota titulo="Observaciones del chofer" texto={datosPedido.obs_chofer} />
          </TarjetaDetalle>
        ) : null}
      </Box>

      {/* Cobros registrados desde la app (pedido y cuenta corriente) */}
      <CobrosPedido idPedido={datosPedido.id_pedido} />
    </Box>
  );
}

export default DetallePedido;
